import * as z from 'zod'
import { FooterSettingsFormValues } from './types'
import {
  buildLinkTargetVariables,
  linkTargetRefine,
  linkTargetSchemaFields,
} from '@/lib/linkTarget'

const footerLinkProfileSchema = z.object({
  language: z.string(),
  label: z.string(),
})

const footerLinkSchema = z.object({
  _key: z.string(),
  ...linkTargetSchemaFields,
  profiles: z.array(footerLinkProfileSchema),
})

const footerColumnProfileSchema = z.object({
  language: z.string(),
  heading: z.string(),
})

const footerColumnSchema = z.object({
  _key: z.string(),
  profiles: z.array(footerColumnProfileSchema),
  links: z.array(footerLinkSchema),
})

/**
 * Validation ONLY — no `.transform()`. `columns[].profiles` and
 * `columns[].links[].profiles` are addressed positionally by `languageId`,
 * so a transform that reshapes/filters either array would desync the
 * resolver's parsed output from the form's field paths (same corruption
 * class `contentBlockFormSchema`'s doc comment warns about). Every seeded
 * profile row (including empty labels/headings) is sent as-is by
 * `buildFooterVariables`.
 *
 * A FACTORY, not a static schema — see `buildNavigationSettingsFormSchema`'s
 * doc for why the "needs a heading" issue path must target
 * `profiles.${activeLanguageId}.heading` rather than a hardcoded index.
 */
export function buildFooterSettingsFormSchema(activeLanguageId: number) {
  return z
    .object({
      columns: z.array(footerColumnSchema),
      footer_address: z.string(),
      footer_phone: z.string(),
      footer_email: z.string(),
      footer_copyright_text: z.string(),
      facebook: z.string(),
      twitter: z.string(),
      instagram: z.string(),
      pinterest: z.string(),
      linkedin: z.string(),
    })
    .superRefine((data, ctx) => {
      data.columns.forEach((column, columnIndex) => {
        const hasHeading = column.profiles.some(p => p.heading?.trim())
        if (!hasHeading) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language needs a heading',
            path: ['columns', columnIndex, 'profiles', activeLanguageId, 'heading'],
          })
        }

        column.links.forEach((link, linkIndex) => {
          linkTargetRefine(link, ctx, ['columns', columnIndex, 'links', linkIndex])
        })
      })
    }) satisfies z.ZodType<FooterSettingsFormValues>
}

/**
 * Build the `updateFooterSettings` mutation variables from validated form
 * values. PURE — no network calls. `columns` is always the FULL ordered
 * array (order = array position, there is no `arrangement` input field).
 * Every scalar is sent on every save (this form always reflects the full
 * current state, never a partial patch) — an emptied field is sent as
 * explicit `null` to clear it, matching the mutation's "omitted = untouched,
 * explicit null = clears" contract.
 */
export function buildFooterVariables(values: FooterSettingsFormValues) {
  return {
    columns: values.columns.map(column => ({
      profiles: column.profiles.map(p => ({ language: p.language, heading: p.heading })),
      links: column.links.map(link => ({
        ...buildLinkTargetVariables(link),
        profiles: link.profiles.map(p => ({ language: p.language, label: p.label })),
      })),
    })),
    facebook: values.facebook || null,
    twitter: values.twitter || null,
    instagram: values.instagram || null,
    pinterest: values.pinterest || null,
    linkedin: values.linkedin || null,
    footer_address: values.footer_address || null,
    footer_phone: values.footer_phone || null,
    footer_email: values.footer_email || null,
    footer_copyright_text: values.footer_copyright_text || null,
  }
}
